import { LatLng } from '@porterin/booking-flow/src/order-review/domain/entities/LatLng';
import { captureExceptionWithContext } from '@porterin/component-library/src/ts-utils/analytics/sentry/Sentry';

import { ExtendedAddress } from '../../domain/entities/ExtendedAddress';

const getAddressTypeDetail = (
  place: google.maps.GeocoderAddressComponent[],
  address_type: string,
): string | undefined => {
  const detail = place.filter(curr => curr.types.includes(address_type));
  return detail.length !== 0 ? detail[0].long_name : undefined;
};

export const resolveGeocodeApi = (
  payload: google.maps.GeocoderRequest,
  on_success: (response: google.maps.GeocoderResponse) => void,
  on_error: (error: any) => void,
) => {
  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode(payload).then(on_success).catch(on_error);
};

export const getCityFromLandmark = (landmark: string): string => {
  const delimiters = [',', '-'];
  for (let delimiter of delimiters) {
    if (landmark.includes(delimiter)) {
      const parts = landmark.split(delimiter).map(part => part.trim());
      if (parts.length > 0) {
        return parts[0];
      }
    }
  }
  return landmark;
};

export const handleAddressDetailsGeocode = (
  place_id: string,
  on_success: (address: ExtendedAddress) => void,
  on_error: (error: any) => void,
  place_description?: string,
) => {
  if (place_id === '') return on_error(new Error('Place ID is required'));

  resolveGeocodeApi(
    { placeId: place_id },
    (response: google.maps.GeocoderResponse) => {
      let place = response.results.shift();
      if (!place)
        return on_error(new Error('No place found for the given place ID'));

      on_success({
        landmark: place_description ?? place.formatted_address,
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
        addressDetails: {
          state: getAddressTypeDetail(
            place.address_components,
            'administrative_area_level_1',
          ),
          city: getCityFromLandmark(
            place_description ?? place.formatted_address,
          ),
          pincode: getAddressTypeDetail(
            place.address_components,
            'postal_code',
          ),
          locality: getAddressTypeDetail(
            place.address_components,
            'sublocality_level_1',
          ),
          subLocality: getAddressTypeDetail(
            place.address_components,
            'sublocality_level_2',
          ),
        },
      });
    },
    error => {
      captureExceptionWithContext('Error in handleAddressDetailsGeocode', {
        error: {
          message: error?.message || String(error),
          stack: error?.stack,
        },
      });
      on_error(error);
    },
  );
};

const getUpdatedPlaceId = (
  place_id: string,
  on_success: (data: string) => void,
  on_error: (error: any) => void,
) => {
  const map = document.getElementById('map') as HTMLDivElement | null;
  if (!map) return on_error('No map instance found');
  const placesService = new window.google.maps.places.PlacesService(map);
  try {
    placesService.getDetails(
      { placeId: place_id, fields: ['place_id'] },
      data => {
        if (!data?.place_id) return on_error('No place id found');
        on_success(data.place_id);
      },
    );
  } catch (error: any) {
    captureExceptionWithContext('Error in getUpdatedPlaceId', {
      error: {
        message: error?.message || String(error),
        stack: error?.stack,
      },
    });
    on_error(error);
  }
};

export const retryAddressDetailsGeocode = (
  place_id: string,
  cb: (address: ExtendedAddress) => void,
  place_description?: string,
  on_error?: (error: any) => void,
) => {
  getUpdatedPlaceId(
    place_id,
    updated_place_id => {
      handleAddressDetailsGeocode(
        updated_place_id,
        address => cb(address),
        error => {
          const errorWithMessage = new Error(
            'Failed to retry geocoding after place ID update',
          );
          if (on_error) {
            on_error(errorWithMessage);
          } else {
            console.error('Retry geocoding failed:', errorWithMessage);
          }
        },
        place_description,
      );
    },
    error => {
      const errorWithMessage = new Error(
        'Failed to get updated place ID for retry',
      );
      captureExceptionWithContext(
        'Error in retryAddressDetailsGeocode - getUpdatedPlaceId',
        {
          error: {
            message: errorWithMessage.message,
            stack: errorWithMessage.stack,
            originalError: error,
          },
        },
      );
      if (on_error) {
        on_error(errorWithMessage);
      } else {
        console.error('Failed to get updated place ID:', errorWithMessage);
      }
    },
  );
};

export async function getExtendedAddressFromLatLng(
  latLng: LatLng,
): Promise<ExtendedAddress> {
  return new Promise<ExtendedAddress>((resolve, reject) => {
    resolveGeocodeApi(
      { location: latLng },
      response => {
        const place = response.results.shift();
        if (!place) {
          const error = new Error('No place found for the given coordinates');
          captureExceptionWithContext(
            'Error in getExtendedAddressFromLatLng - No place found',
            {
              error: {
                message: error.message,
                stack: error.stack,
              },
            },
          );
          return reject(error);
        }

        const details = {
          state: getAddressTypeDetail(
            place.address_components,
            'administrative_area_level_1',
          ),
          city: getCityFromLandmark(place.formatted_address),
          pincode: getAddressTypeDetail(
            place.address_components,
            'postal_code',
          ),
          locality: getAddressTypeDetail(
            place.address_components,
            'sublocality_level_1',
          ),
          subLocality: getAddressTypeDetail(
            place.address_components,
            'sublocality_level_2',
          ),
        };

        resolve({
          landmark: place.formatted_address,
          location: latLng,
          addressDetails: details,
        });
      },
      error => {
        const geocodeError =
          error instanceof Error ? error : new Error(String(error));
        captureExceptionWithContext('Error in getExtendedAddressFromLatLng', {
          error: {
            message: geocodeError.message,
            stack: geocodeError.stack,
          },
        });
        reject(geocodeError);
      },
    );
  });
}
